<template>
  <div class="sub-game-tab">
    <van-nav-bar class="sub-menus" @click-right="onOpenPopupMenus" v-if="menus.length > 0">
      <template #right>
        <SvgIcon icon-name="list" width="58" current-color />
      </template>
      <template #title>
        <van-tabs
          ref="subMenuRef"
          v-model:active="activeHomeSubMenuTab"
          :line-height="0"
          shrink
          @change="onSubTabChange"
          @click-tab="onTabClick"
        >
          <van-tab v-for="item in menus" :name="item.key" :key="item.key">
            <template #title>
              <div class="sub-menu-item">
                <div class="tab-icon" v-if="item.icon">
                  <SvgIcon :icon-name="item.icon" :height="48" current-color />
                </div>
                <div class="tab-icon" :data-src="item.darkImgSrc" v-else>
                  <img class="light-icon" :src="item.colorImgSrc" style="height: 100%" />
                </div>
                <span class="tab-name text-ellipsis">{{ item.name }}</span>
              </div>
            </template>
          </van-tab>
        </van-tabs>
      </template>
    </van-nav-bar>
    <search-bar />
  </div>

  <van-popup class="popup-menus" v-model:show="showPopupMenus" position="bottom" teleport="body" round>
    <div class="popup-header">
      <span>{{ $t('平台选择') }}</span>
      <van-button class="close-btn" icon="cross" size="small" @click="onClosePopupMenus" />
    </div>
    <div class="popup-content">
      <van-grid :gutter="10" :column-num="2">
        <van-grid-item v-for="item in items" :key="item.key">
          <div class="menu-btn" :class="{ active: activeHomeSubMenuTab === item.key }" @click="onSelectSubMenu(item)">
            <div class="btn-icon">
              <Image fit="contain" :src="item.colorImgSrc" />
            </div>
            <div class="btn-title van-ellipsis">{{ item.name }}</div>
          </div>
        </van-grid-item>
      </van-grid>
      <div class="van-safe-area-bottom"></div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { filter, toString } from 'lodash-es';
import SearchBar from './SearchBar.vue';
import { storeToRefs, useAppStore, useGameStore } from '@star/stores';

type Props = {
  menus: any[];
  activeMenu?: string;
};

const props = defineProps<Props>();

const emit = defineEmits(['onSubChange', 'clickTab']);

const gameStore = useGameStore();
const { activeHomeSubMenuTab } = storeToRefs(gameStore);
const { isPageLoading } = storeToRefs(useAppStore());
const showPopupMenus = ref<boolean>(false);
const subMenuRef = ref<any>(null);

const items = computed(() => {
  return filter(props.menus, item => {
    return item.key < 10000;
  });
});

const onSubTabChange = (key: string) => {
  // console.log(key, 'onSubTabChange -----');
  emit('onSubChange', toString(key));
};

const onTabClick = (e: any) => {
  emit('clickTab');
};

const onOpenPopupMenus = () => {
  // console.log('onSelectMore');
  showPopupMenus.value = true;
};

const onClosePopupMenus = () => {
  // console.log('onSelectMore');
  showPopupMenus.value = false;
};

const onSelectSubMenu = (item: any) => {
  // console.log('onSelectSubMenu');
  onSubTabChange(item.key);
  onClosePopupMenus();
};

const tabMenuScroll = () => {
  const tab_offsetLeft = subMenuRef.value?.$el?.querySelector('.van-tab--active')?.offsetLeft;
  const tab_offsetWidth = subMenuRef.value?.$el?.querySelector('.van-tab--active')?.offsetWidth;
  const nav_offsetWidth = subMenuRef.value?.$el?.querySelector('.van-tabs__nav')?.offsetWidth;
  const left = tab_offsetLeft - (nav_offsetWidth - tab_offsetWidth) / 2;
  subMenuRef.value?.$el?.querySelector('.van-tabs__nav')?.scrollTo?.({
    left: left ?? 0,
    behavior: 'smooth',
  });
};

watch(
  () => isPageLoading.value,
  async (value, oldValue) => {
    if (!value && oldValue) {
      tabMenuScroll();
    }
  }
);

onMounted(async () => {});
</script>

<style lang="less" scoped>
.sub-game-tab {
  padding: 40px 0 20px;
  background-color: var(--theme-backgroud-color);

  .sub-menu-item {
    min-width: 270px;
    height: 96px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 38px;
    font-weight: 380;
    cursor: pointer;
    border-radius: 20px;
    padding: 0 24px;
    white-space: nowrap;
    background-color: #f6f6f7;
    color: #b6b8bd;
    overflow: hidden;

    .tab-icon {
      min-width: 50px;
      height: 50px;
      flex: none;

      &.svg-icon {
        margin-top: -9px;
      }

      :deep(.van-image__error) {
        display: none;
      }
    }

    //.svg-icon {
    //  color: #b6b8bd;
    //}
  }

  :deep(.van-tab) {
    line-height: normal;

    .van-tabs__wrap {
      height: auto;
    }

    .svg-icon {
      margin-top: -15px;
    }

    .tab-name {
      margin-left: 12px;
      font-size: 36px;
      font-weight: 600;
      color: #b6b8bd;
      line-height: 1.2;
    }

    &.van-tab--active {
      .sub-menu-item {
        background-color: var(--theme-btn-primary-background);
        color: #fff;

        .svg-icon,
        .tab-name {
          color: #fff;
        }
      }
    }
  }

  :deep(.sub-menus) {
    padding-bottom: 40px;
    line-height: normal;

    .van-nav-bar__left,
    .van-nav-bar__right {
      padding: 0 0 0 18px;
    }

    .van-nav-bar__title {
      max-width: 100%;
      margin: 0 90px 0 0;
    }

    .van-nav-bar__content {
      height: auto;
    }

    .van-tabs__wrap {
      height: auto;

      .van-tabs__nav {
        padding: 0;
      }

      .van-tabs__line {
        bottom: 0;
      }
    }

    .van-tabs__line {
      display: none !important;
    }

    &::after {
      display: none !important;
    }
  }
}

.popup-menus {
  overflow: hidden;
  height: 50%;
  display: flex;
  flex-direction: column;

  .popup-header {
    height: 150px;
    line-height: 150px;
    text-align: center;
    font-size: 54px;
    font-weight: 500;
    color: #000;
    position: relative;
    flex: none;

    .close-btn {
      position: absolute;
      top: 30px;
      right: 15px;
      font-size: 48px;
      border: none;
      color: #cecece;
    }
  }

  .popup-content {
    padding-top: 30px;
    padding-bottom: 75px;
    //max-height: calc(100% - 150px);
    overflow-y: auto;
    overscroll-behavior: contain;
    flex: 1;

    .menu-btn {
      width: 100%;
      height: 172px;
      background-color: #f6f6f7;
      border-radius: 20px;
      border: none;
      font-size: 48px;
      font-weight: 600;
      color: #333;
      display: flex;
      align-items: center;
      white-space: nowrap;
      padding: 0 36px;

      &.active {
        color: var(--theme-btn-plain-background);
        background-color: var(--theme-btn-primary-background);
      }

      .btn-icon {
        width: 35% !important;
        height: 68px !important;
        margin-right: 18px;
        flex: none;
        //margin-top: -3px;
      }

      .btn-title {
        flex: 1;
      }

      :deep(.van-image__error) {
        background: transparent;
        border-radius: 0;
      }
    }

    :deep(.van-grid-item__content) {
      padding: 0;

      &::after {
        border: none;
      }
    }
  }
}
</style>
